import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    ParamSchema,
    ProcessArch,
    deleteSchema,
    listProcessArchs,
    listSchemaBindings,
    listSchemas,
    upsertSchema,
} from '../../api/parameter';
import { listAllDevices, Device } from '../../api/device';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import BindingList, { BindingListItem } from '../../components/BindingList';
import Modal from '../../components/Modal';
import SchemaEditModal, {
    FieldSpec,
    parseFields,
} from '../parameter/SchemaEditModal';
import SchemaPreviewModal from '../parameter/SchemaPreviewModal';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import { useSchemaKeyLabel } from '../../utils/useSchemaKeyLabel';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';
import { Layout, parseLayout, stringifyLayout } from '../../utils/fieldGroups';

// 参-04 统一 Schema 模板管理页 (管理员管理).
// 模板键 (2026-11-01) = 框架 + 设备 + 加工模式 + 加工类型 (加工模式/加工类型空=通配); 新建必须选设备,
// 一个机型多台设备就各建一份 (「复制到设备」). 没有设备段的是存量按机型建的模板: 只能改字段/复制/删, 待拆分.
// 参数录入选定键后按"最特异匹配"自动命中模板, 字段集只能来源模板 (字段本身来源 Schema 字段字典 SSOT).
// 权限: 登录即可操作, 写操作统一走审批工单.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

interface EditorState {
    isNew: boolean;
    deviceKey: string;
    /** 存量未拆分模板的机型 (编辑它时定位用); 新建恒为空 */
    family: string;
    groupCode: string;
    processType: string;
    fields: FieldSpec[];
    /** 分组布局 (2026-10-23); null = 未启用分组 */
    layout: Layout;
}

function SchemaTemplateAdmin() {
    const canDelete = useCan('param_schema', 'delete');
    const canCreate = useCan('param_schema', 'create');
    const canUpdate = useCan('param_schema', 'update');
    const history = useHistory();
    // 参数详情页"字段来自哪张模板"跳转过来时带 ?arch/deviceKey/group/processType, 预选框架并预置筛选
    const initQ = new URLSearchParams(useLocation().search);
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [arch, setArch] = useState(initQ.get('arch') || '');
    const [list, setList] = useState<ParamSchema[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 设备全集 (一次拉齐); 候选按当前框架收窄, 见 devices
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    // 加工模式/加工类型字典 + 模板键展示名 (与参数批量导入弹窗共用同一份 hook)
    const { groups, subProcs, groupName, subProcLabel, keyLabel } =
        useSchemaKeyLabel(arch);
    // 设备候选 (新建 / 复制到设备): 本框架的设备 + 本框架下已有模板的其它框架设备
    // (参数行可跨框架挂设备, 如刀切行挂激光框机 MLM, 这些设备在本框架已有模板, 照样能继续建 / 复制), 本框架的排前面
    const devices = useMemo(() => {
        const tplDevices = new Set(list.map((t) => t.deviceKey).filter(Boolean));
        return allDevices
            .filter((d) => d.archCode === arch || tplDevices.has(d.deviceKey))
            .sort(
                (a, b) =>
                    Number(b.archCode === arch) - Number(a.archCode === arch),
            );
    }, [allDevices, arch, list]);
    const deviceLabel = useCallback(
        (d: Device) =>
            `${d.name && d.name !== d.deviceKey ? `${d.name} (${d.deviceKey})` : d.deviceKey}${
                d.archCode === arch ? '' : ` · 其它框架 ${d.archCode}`
            }`,
        [arch],
    );
    // 新建弹窗的键维度下拉候选
    const editorDeviceOpts = useMemo(
        () => devices.map((d) => ({ value: d.deviceKey, label: deviceLabel(d) })),
        [devices, deviceLabel],
    );
    const editorGroupOpts = useMemo(
        () =>
            zhOptions(
                groups,
                (g) => g.code,
                (g) => g.nameZh,
            ),
        [groups],
    );
    const editorSubProcOpts = useMemo(
        () => subProcs.map((s) => ({ value: s.code, label: s.label })),
        [subProcs],
    );
    // 键选择弹窗 (新建) 与字段编辑弹窗
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [fieldsOpen, setFieldsOpen] = useState(false);
    // 只读预览 (不需要编辑权限)
    const [previewTpl, setPreviewTpl] = useState<ParamSchema | null>(null);
    // 关联参数清单模态 (删除受阻时弹出, 与材料/设备模板同一组件)
    const [listModal, setListModal] = useState<{
        title: string;
        subtitle: string;
        items: BindingListItem[];
    } | null>(null);
    // 正在校验关联的模板 id (删除按钮转"校验中…")
    const [removing, setRemoving] = useState(0);
    // 复制到设备弹窗 (表格行「复制到设备」): 以本模板的字段/布局为样板, 给勾选的每台设备各建一份 (各一张工单)
    const [copyTpl, setCopyTpl] = useState<ParamSchema | null>(null);
    const [copyKeys, setCopyKeys] = useState<string[]>([]);
    const [copySaving, setCopySaving] = useState(false);
    // 列表筛选 (关键词 / 设备 / 加工模式 / 加工类型); ANY = 仅看该维度为空的模板 (设备 ANY = 存量待拆分)
    // 批量操作 (2026-09-10): 模板 targetCode 与后端 schemaKeyTarget 同格式 —— 按设备建的 arch//group/processType/deviceKey
    // (机型随设备, 不进键), 存量模板 arch/family/group/processType; 有参数行命中时后端拒删.
    const schemaTarget = (t: {
        deviceKey?: string;
        family?: string;
        groupCode?: string;
        processType?: string;
    }) =>
        t.deviceKey
            ? [arch, '', t.groupCode || '', t.processType || '', t.deviceKey].join('/')
            : [arch, t.family || '', t.groupCode || '', t.processType || ''].join('/');

    const [fKeyword, setFKeyword] = useState('');
    const [fDevice, setFDevice] = useState(initQ.get('deviceKey') || '');
    const [fGroup, setFGroup] = useState(initQ.get('group') || '');
    const [fProc, setFProc] = useState(initQ.get('processType') || '');
    // 切框架才清筛选: 记住上一个 arch 与之比较, 而不是"是不是首次跑" —
    // StrictMode 下 effect 双跑, 用 firstRun 标记会在第二跑把 URL 预置的筛选抹掉 (深链跳转失效)
    const prevArch = useRef(arch);

    useEffect(() => {
        listProcessArchs()
            .then((as) => {
                const usable = as.filter((a) => a.paramTable);
                setArchs(usable);
                setArch((cur) => cur || usable[0]?.code || '');
            })
            .catch(() => setArchs([]));
    }, []);

    const load = useCallback(async () => {
        if (!arch) return;
        setLoading(true);
        setError('');
        try {
            const rows = await listSchemas(arch);
            setList(rows);
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [arch]);

    useEffect(() => {
        load();
    }, [load]);

    // 机型/子工艺候选随框架变化
    useEffect(() => {
        if (!arch) return;
        if (prevArch.current !== arch) {
            setFKeyword('');
            setFDevice('');
            setFGroup('');
            setFProc('');
        }
        prevArch.current = arch;
    }, [arch]);

    useEffect(() => {
        listAllDevices()
            .then(setAllDevices)
            .catch(() => setAllDevices([]));
    }, []);

    const fieldCount = (t: ParamSchema): number => parseFields(t.fields).length;

    // 后端已按 设备 → 机型 → 加工模式 → 加工类型 排序 (存量待拆分的排最前)
    const sorted = list;

    // 筛选下拉候选取自当前列表实际出现的键值 (保证选了必有结果)
    const distinct = (pick: (t: ParamSchema) => string) =>
        Array.from(new Set(list.map(pick).filter(Boolean))).sort();
    const deviceOpts = useMemo(() => distinct((t) => t.deviceKey), [list]); // eslint-disable-line react-hooks/exhaustive-deps
    const groupOpts = useMemo(() => distinct((t) => t.groupCode), [list]); // eslint-disable-line react-hooks/exhaustive-deps
    const procOpts = useMemo(() => distinct((t) => t.processType), [list]); // eslint-disable-line react-hooks/exhaustive-deps

    // ANY = 仅看该维度通配 ('' 存库) 的模板; '' = 不过滤
    const matchDim = (val: string, filter: string) =>
        !filter || (filter === 'ANY' ? val === '' : val === filter);
    const visible = useMemo(() => {
        const kw = fKeyword.trim().toLowerCase();
        return sorted.filter(
            (t) =>
                matchDim(t.deviceKey, fDevice) &&
                matchDim(t.groupCode, fGroup) &&
                matchDim(t.processType, fProc) &&
                (!kw || keyLabel(t).toLowerCase().includes(kw)),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorted, fKeyword, fDevice, fGroup, fProc, groupName, subProcLabel]);
    const filtering = !!(fKeyword.trim() || fDevice || fGroup || fProc);

    const sel = useBatchSelect(
        useMemo(
            () => visible.map(schemaTarget),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [visible, arch],
        ),
        [arch, fKeyword, fDevice, fGroup, fProc].join('|'),
    );

    const openNew = () =>
        setEditor({
            isNew: true,
            deviceKey: '',
            family: '',
            groupCode: '',
            processType: '',
            fields: [],
            layout: null,
        });

    const openEdit = (t: ParamSchema) => {
        setEditor({
            isNew: false,
            deviceKey: t.deviceKey,
            family: t.family,
            groupCode: t.groupCode,
            processType: t.processType,
            fields: parseFields(t.fields),
            layout: parseLayout(t.layout),
        });
        setFieldsOpen(true);
    };

    // 删除前按需查关联: 有参数行在用就弹清单挡住 (清单可点进参数详情), 没有才让删.
    // 关联只在这一刻算 (列表不带计数, 避免每次开页都跑全框架聚合).
    const onRemove = async (t: ParamSchema) => {
        setRemoving(t.id);
        try {
            const r = await listSchemaBindings(arch, t);
            if (r.total > 0) {
                setListModal({
                    title: `模板「${keyLabel(t)}」不能删除`,
                    subtitle: `${r.total} 条参数行正命中本模板${
                        r.truncated ? ` (仅列前 ${r.items.length} 条)` : ''
                    }, 请先改这些行的键或删除它们 · 点击任一项跳转参数详情`,
                    items: r.items,
                });
                return;
            }
        } catch (e) {
            alertDialog(`校验关联参数失败: ${errMsg(e)}`); // 查不到就不放行, 交给后端删除保护也无从触发
            return;
        } finally {
            setRemoving(0);
        }
        if (
            !(await confirmDialog(
                `确认删除模板「${keyLabel(t)}」? 当前没有参数行命中它.`,
            ))
        )
            return;
        try {
            const res = await deleteSchema(arch, t);
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };

    const onConfirmFields = async (fields: FieldSpec[], layout: Layout) => {
        if (!editor) return;
        // 分组布局每次都随保存传 (upsert 整行覆盖)
        const res = await upsertSchema(
            arch,
            JSON.stringify(fields),
            editor,
            stringifyLayout(layout),
        );
        setFieldsOpen(false);
        setEditor(null);
        await load();
        alertApprovalIfPending(res.message);
    };

    // 复制到设备: 每台设备一张 upsert 工单 (同加工模式/加工类型, 字段与布局照抄样板);
    // 目标设备已有同键模板时 upsert 会覆盖其字段, 所以候选里不列已有同键模板的设备.
    const copyTargets = useMemo(() => {
        if (!copyTpl) return [];
        const taken = new Set(
            list
                .filter(
                    (t) =>
                        t.deviceKey &&
                        t.groupCode === copyTpl.groupCode &&
                        t.processType === copyTpl.processType,
                )
                .map((t) => t.deviceKey),
        );
        return devices.filter((d) => !taken.has(d.deviceKey));
    }, [copyTpl, list, devices]);
    const saveCopy = async () => {
        if (!copyTpl || copyKeys.length === 0) return;
        setCopySaving(true);
        const done: string[] = [];
        try {
            let lastMsg = '';
            for (const deviceKey of copyKeys) {
                const res = await upsertSchema(
                    arch,
                    copyTpl.fields,
                    {
                        deviceKey,
                        groupCode: copyTpl.groupCode,
                        processType: copyTpl.processType,
                    },
                    copyTpl.layout ?? null,
                );
                done.push(deviceKey);
                lastMsg = res.message;
            }
            setCopyTpl(null);
            await load();
            alertApprovalIfPending(lastMsg);
        } catch (e) {
            alertDialog(
                `复制失败: ${errMsg(e)}${done.length ? `\n已提交: ${done.join(', ')}` : ''}`,
            );
        } finally {
            setCopySaving(false);
        }
    };

    const activeArch = archs.find((a) => a.code === arch);

    return (
        <div className="space-y-4">
            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        {archs.map((a) => (
                            <button
                                key={a.code}
                                type="button"
                                className={`px-3 py-1.5 rounded-lg text-sm border ${
                                    a.code === arch
                                        ? 'bg-brand-600 text-white border-brand-600'
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                }`}
                                onClick={() => setArch(a.code)}
                            >
                                {a.icon} {a.label}
                            </button>
                        ))}
                        <span
                            className="text-xs text-slate-400 whitespace-nowrap cursor-help"
                            title="模板键 = 设备 / 加工模式 / 加工类型 (加工模式/加工类型空 = 通配)。一个机型多台设备各建一份; 参数录入选定键后按最特异匹配自动命中, 字段集只能来源模板; 字段本身必须先在「字段」tab 登记 (xtool-schema SSOT)。"
                        >
                            ⓘ 模板 (
                            {filtering
                                ? `${visible.length} / ${list.length}`
                                : list.length}
                            )
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <div title="按设备筛选">
                            <SearchSelect
                                className="w-44"
                                value={fDevice}
                                onChange={(v) => setFDevice(v)}
                                placeholder="全部设备"
                                options={[
                                    {
                                        value: 'ANY',
                                        label: '存量按机型 (待拆分)',
                                    },
                                    ...deviceOpts.map((k) => ({
                                        value: k,
                                        label: k,
                                    })),
                                ]}
                                clearable
                            />
                        </div>
                        <div title="按加工模式筛选">
                            <SearchSelect
                                className="w-36"
                                value={fGroup}
                                onChange={(v) => setFGroup(v)}
                                placeholder="全部加工模式"
                                options={[
                                    {
                                        value: 'ANY',
                                        label: '任意加工模式 (通配)',
                                    },
                                    ...groupOpts.map((g) => ({
                                        value: g,
                                        label: groupName(g),
                                    })),
                                ]}
                                clearable
                            />
                        </div>
                        <div title="按加工类型筛选">
                            <SearchSelect
                                className="w-36"
                                value={fProc}
                                onChange={(v) => setFProc(v)}
                                placeholder="全部加工类型"
                                options={[
                                    {
                                        value: 'ANY',
                                        label: '任意加工类型 (通配)',
                                    },
                                    ...procOpts.map((p) => ({
                                        value: p,
                                        label: subProcLabel(p),
                                    })),
                                ]}
                                clearable
                            />
                        </div>
                        <input
                            className="inp w-44"
                            placeholder="搜索模板键"
                            value={fKeyword}
                            onChange={(e) => setFKeyword(e.target.value)}
                        />
                        {filtering && (
                            <button
                                type="button"
                                className="btn-ghost text-slate-400"
                                title="清空筛选"
                                onClick={() => {
                                    setFKeyword('');
                                    setFDevice('');
                                    setFGroup('');
                                    setFProc('');
                                }}
                            >
                                ✕ 重置
                            </button>
                        )}
                        <BatchActions
                            resource="param_schema"
                            entityLabel="字段模板"
                            deleteKeyField="archCode"
                            buildDeleteData={(target) => {
                                const [
                                    archCode,
                                    family,
                                    groupCode,
                                    processType,
                                    deviceKey = '',
                                ] = target.split('/');
                                return {
                                    archCode,
                                    deviceKey,
                                    family,
                                    groupCode,
                                    processType,
                                };
                            }}
                            selected={sel.selectedKeys}
                            onClear={sel.clear}
                            onDone={load}
                        />
                        <button
                            type="button"
                            className="btn-primary text-xs whitespace-nowrap"
                            onClick={openNew}
                            hidden={!canCreate}
                        >
                            + 新建模板
                        </button>
                    </div>
                </div>
                {loading ? (
                    <div className="text-center text-slate-400 text-sm py-8">
                        加载中…
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs text-slate-500 text-left">
                                <th className="py-1.5 w-8">
                                    <HeaderCheckbox
                                        hidden={!canDelete}
                                        sel={sel}
                                    />
                                </th>
                                <th className="py-1.5 w-16">模板 ID</th>
                                <th className="py-1.5">
                                    模板键 (设备 · 加工模式 · 加工类型)
                                </th>
                                <th>字段数</th>
                                <th className="text-right">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((t) => {
                                const isDefault =
                                    !t.deviceKey &&
                                    !t.family &&
                                    !t.groupCode &&
                                    !t.processType;
                                return (
                                    <tr
                                        key={t.id}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="py-2">
                                            <RowCheckbox
                                                hidden={!canDelete}
                                                sel={sel}
                                                k={schemaTarget(t)}
                                            />
                                        </td>
                                        {/* 模板 ID: 导入模板下载 / 排障对单都按这个号说话 */}
                                        <td className="py-2 font-mono text-xs text-slate-500">
                                            {t.id}
                                        </td>
                                        <td className="py-2">
                                            {isDefault ? (
                                                <span className="tag bg-slate-100 text-slate-600">
                                                    框架默认 (兜底)
                                                </span>
                                            ) : (
                                                <span>{keyLabel(t)}</span>
                                            )}
                                            {!t.deviceKey && !isDefault && (
                                                <span
                                                    className="tag ml-1.5 text-[10px] bg-amber-50 text-amber-600 cursor-help"
                                                    title={`存量按机型建的模板 (过渡期按机型命中${
                                                        t.deviceKeys?.length
                                                            ? `, 限设备 ${t.deviceKeys.join(' / ')}`
                                                            : ''
                                                    }): 用「复制到设备」给该机型每台设备各建一份后再删本模板`}
                                                >
                                                    待拆分
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-xs text-slate-500">
                                            {fieldCount(t)} 个字段
                                        </td>
                                        <td className="text-right text-xs space-x-2 whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() => setPreviewTpl(t)}
                                            >
                                                预览
                                            </button>
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() => openEdit(t)}
                                                hidden={!canUpdate}
                                            >
                                                编辑字段
                                            </button>
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="以本模板为样板, 给其它设备各建一份 (同加工模式/加工类型)"
                                                onClick={() => {
                                                    setCopyTpl(t);
                                                    setCopyKeys([]);
                                                }}
                                                hidden={!canCreate}
                                            >
                                                复制到设备
                                            </button>
                                            <button
                                                type="button"
                                                className="text-rose-500 hover:underline disabled:text-slate-300 disabled:no-underline"
                                                title="删除本模板 (先校验有没有参数行在用)"
                                                disabled={removing === t.id}
                                                onClick={() => onRemove(t)}
                                            >
                                                {removing === t.id
                                                    ? '校验中…'
                                                    : '删除'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {visible.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="py-6 text-center text-slate-400 text-xs"
                                    >
                                        {list.length === 0
                                            ? '该框架暂无模板'
                                            : '无匹配模板, 调整筛选条件试试'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* 新建: 先选模板键 */}
            {editor && editor.isNew && !fieldsOpen && (
                <div
                    className="fixed inset-0 !m-0 z-40 grid place-items-center"
                    style={{ background: 'rgba(15,23,42,.45)' }}
                >
                    <div className="bg-white rounded-xl shadow-xl p-5 w-[520px] space-y-3">
                        <div className="text-sm font-semibold">
                            新建模板 · 选择模板键
                        </div>
                        <div className="text-xs text-slate-500">
                            模板按设备建: 设备必选; 加工模式/加工类型留空 =
                            该维度通配 (已存在同键模板时会覆盖其字段)。
                        </div>
                        <div>
                            <label className="field-label">设备 *</label>
                            <SearchSelect
                                className="w-full"
                                value={editor.deviceKey}
                                onChange={(v) =>
                                    setEditor({ ...editor, deviceKey: v })
                                }
                                placeholder="选择设备"
                                options={editorDeviceOpts}
                            />
                        </div>
                        <div>
                            <label className="field-label">加工模式</label>
                            <SearchSelect
                                className="w-full"
                                value={editor.groupCode}
                                onChange={(v) =>
                                    setEditor({ ...editor, groupCode: v })
                                }
                                placeholder="任意加工模式 (通配)"
                                options={editorGroupOpts}
                                clearable
                            />
                        </div>
                        <div>
                            <label className="field-label">加工类型</label>
                            <SearchSelect
                                className="w-full"
                                value={editor.processType}
                                onChange={(v) =>
                                    setEditor({ ...editor, processType: v })
                                }
                                placeholder="任意子工艺 (通配)"
                                options={editorSubProcOpts}
                                clearable
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setEditor(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={!editor.deviceKey}
                                title={editor.deviceKey ? '' : '先选设备'}
                                onClick={() => {
                                    // 底稿: 既有同键模板 (存在即编辑语义) → 该设备机型的存量同模式/类型模板 → 空
                                    const sameKey = (t: ParamSchema) =>
                                        t.groupCode === editor.groupCode &&
                                        t.processType === editor.processType;
                                    const family = devices.find(
                                        (d) => d.deviceKey === editor.deviceKey,
                                    )?.family;
                                    const base =
                                        list.find(
                                            (t) =>
                                                t.deviceKey ===
                                                    editor.deviceKey &&
                                                sameKey(t),
                                        ) ||
                                        list.find(
                                            (t) =>
                                                !t.deviceKey &&
                                                !!family &&
                                                t.family === family &&
                                                sameKey(t),
                                        );
                                    setEditor({
                                        ...editor,
                                        fields: base
                                            ? parseFields(base.fields)
                                            : [],
                                        // 新建模板以同键/框架默认为底稿时连同其分组一起带上
                                        layout: base
                                            ? parseLayout(base.layout)
                                            : null,
                                    });
                                    setFieldsOpen(true);
                                }}
                            >
                                下一步: 编辑字段
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 字段编辑 (复用 SchemaEditModal, 字段只能来源 Schema 字段字典) */}
            {editor && (
                <SchemaEditModal
                    open={fieldsOpen}
                    scope="template"
                    subtitle={`${activeArch?.label || arch} · ${keyLabel(editor)}`}
                    initialFields={editor.fields}
                    initialLayout={editor.layout}
                    onClose={() => {
                        setFieldsOpen(false);
                        setEditor(null);
                    }}
                    onConfirm={onConfirmFields}
                />
            )}

            {/* 只读预览: 模板有哪些字段 */}
            {previewTpl && (
                <SchemaPreviewModal
                    title={`模板 #${previewTpl.id} · 字段预览`}
                    subtitle={`${activeArch?.label || arch} · ${keyLabel(previewTpl)}`}
                    fields={parseFields(previewTpl.fields)}
                    layout={parseLayout(previewTpl.layout)}
                    dictScope={arch}
                    onClose={() => setPreviewTpl(null)}
                />
            )}

            {/* 复制到设备: 以本模板为样板, 给勾选的设备各建一份 */}
            {copyTpl && (
                <Modal
                    open
                    title={`复制模板「${keyLabel(copyTpl)}」到设备`}
                    subtitle="给勾选的每台设备各建一份同加工模式/加工类型的模板 (字段与分组照抄), 每台一张审批工单; 已有同键模板的设备不列出"
                    onClose={() => setCopyTpl(null)}
                    width={560}
                >
                    <div className="space-y-3">
                        <div className="flex gap-2 text-xs justify-end">
                            {copyTpl.family && (
                                <button
                                    type="button"
                                    className="text-brand-600 hover:underline"
                                    onClick={() =>
                                        setCopyKeys(
                                            copyTargets
                                                .filter(
                                                    (d) =>
                                                        d.family ===
                                                        copyTpl.family,
                                                )
                                                .map((d) => d.deviceKey),
                                        )
                                    }
                                >
                                    选中机型 {copyTpl.family} 的全部设备
                                </button>
                            )}
                            <button
                                type="button"
                                className="text-slate-400 hover:text-slate-600"
                                onClick={() => setCopyKeys([])}
                            >
                                清空
                            </button>
                        </div>
                        {copyTargets.length === 0 ? (
                            <div className="text-xs text-slate-300 py-2">
                                没有可复制的设备 (都已有同键模板)
                            </div>
                        ) : (
                            <div className="max-h-64 overflow-auto border border-slate-100 rounded-md p-2 grid grid-cols-2 gap-1">
                                {copyTargets.map((d) => {
                                    const checked = copyKeys.includes(
                                        d.deviceKey,
                                    );
                                    return (
                                        <label
                                            key={d.deviceKey}
                                            className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"
                                            title={d.deviceKey}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() =>
                                                    setCopyKeys(
                                                        checked
                                                            ? copyKeys.filter(
                                                                  (k) =>
                                                                      k !==
                                                                      d.deviceKey,
                                                              )
                                                            : [
                                                                  ...copyKeys,
                                                                  d.deviceKey,
                                                              ],
                                                    )
                                                }
                                            />
                                            {/* 名称后带 device_key: 同机型多台设备重名 (四台 D1 只差瓦数) */}
                                            <span className="truncate">
                                                {deviceLabel(d)}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <div className="text-[11px] text-slate-400">
                            已选 {copyKeys.length} 台
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setCopyTpl(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={copySaving || copyKeys.length === 0}
                                onClick={saveCopy}
                            >
                                {copySaving ? '提交中…' : '提交'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* 关联参数清单 (与材料/设备模板同一个 BindingList) */}
            {listModal && (
                <Modal
                    open
                    title={listModal.title}
                    subtitle={listModal.subtitle}
                    onClose={() => setListModal(null)}
                    width={560}
                >
                    <BindingList
                        items={listModal.items}
                        unit="参数行"
                        onPick={(code) =>
                            history.push(
                                `/param-sets/manage?arch=${encodeURIComponent(arch)}&id=${encodeURIComponent(code)}`,
                            )
                        }
                    />
                </Modal>
            )}
        </div>
    );
}

export default SchemaTemplateAdmin;
